import { Search } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { NAVIGATION_COPY } from '@/locales/en'
import { useInstrumentSearch } from './instrumentSearch/useInstrumentSearch'
import { DesktopSearchResults } from './instrumentSearch/DesktopSearchResults'
import { MobileSearchResults } from './instrumentSearch/MobileSearchResults'

type InstrumentSearchProps = {
    onItemClick?: () => void
    variant?: 'desktop' | 'mobile-sheet'
    autoFocus?: boolean
}

export default function InstrumentSearch({
    onItemClick,
    variant = 'desktop',
    autoFocus = false,
}: InstrumentSearchProps) {
    const inputRef = useRef<HTMLInputElement>(null)
    const isDesktop = variant === 'desktop'
    const {
        searchTerm,
        searching,
        searchResult,
        showPopover,
        setShowPopover,
        clearSearch,
        onSearchChange,
        onResultClick,
    } = useInstrumentSearch({
        isDesktop,
        onItemClick,
    })
    const shouldShowResults = searching || searchTerm.length >= 2

    useEffect(() => {
        if (!autoFocus || variant !== 'mobile-sheet') return

        const focusOnFrame = requestAnimationFrame(() => {
            inputRef.current?.focus()
        })

        return () => cancelAnimationFrame(focusOnFrame)
    }, [autoFocus, variant])

    if (variant === 'mobile-sheet') {
        return (
            <div className="w-full px-6 pt-6 flex flex-col gap-6 h-full">
                <div className="w-full flex flex-col gap-4">
                    <div className="w-full flex flex-col gap-1">
                        <label
                            htmlFor="mobile-instrument-search"
                            className="w-full text-content-primary text-sm font-normal leading-5"
                        >
                            {NAVIGATION_COPY.instrumentSearch.title}
                        </label>
                        <form
                            className="w-full h-10"
                            onSubmit={(e) => {
                                e.preventDefault()
                            }}
                        >
                            <div className="w-full h-full px-4 py-1 bg-white rounded-[100px] border border-border-outline flex items-center gap-3">
                                <Search className="w-4 h-4 text-brand-forest" />
                                <input
                                    id="mobile-instrument-search"
                                    ref={inputRef}
                                    type="text"
                                    value={searchTerm}
                                    name="search-term"
                                    onChange={onSearchChange}
                                    className="w-full flex-1 bg-transparent outline-none text-content-primary text-base font-normal leading-6"
                                    placeholder={NAVIGATION_COPY.instrumentSearch.placeholder}
                                    aria-label={NAVIGATION_COPY.instrumentSearch.title}
                                />
                            </div>
                        </form>
                    </div>

                    {shouldShowResults && (
                        <div className="w-full p-2 bg-white rounded-2xl border border-border-outline flex flex-col gap-2 max-h-[60vh] overflow-y-auto overscroll-contain">
                            <MobileSearchResults
                                searching={searching}
                                searchResult={searchResult}
                                onResultClick={onResultClick}
                            />
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <>
            <Popover open={showPopover}>
                <PopoverTrigger asChild>
                    <form
                        className="w-[300px]"
                        onSubmit={(e) => {
                            e.preventDefault()
                        }}
                    >
                        <div className="relative">
                            <Search className="absolute top-2 left-4" />
                            <input
                                type="text"
                                value={searchTerm}
                                name="search-term"
                                onChange={onSearchChange}
                                className="w-full px-12 py-[7px] border border-gray-400 rounded-full"
                                placeholder={NAVIGATION_COPY.instrumentSearch.placeholder}
                                aria-label={NAVIGATION_COPY.instrumentSearch.title}
                            />
                        </div>
                    </form>
                </PopoverTrigger>
                <PopoverContent
                    className="w-[300px] p-0 min-h-14 max-h-[60vh] overflow-y-auto flex rounded"
                    align="center"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onInteractOutside={() => {
                        setShowPopover(false)
                        clearSearch()
                    }}
                >
                    <DesktopSearchResults
                        searching={searching}
                        searchResult={searchResult}
                        onResultClick={onResultClick}
                    />
                </PopoverContent>
            </Popover>
        </>
    )
}
